import { useState } from 'react';
import type { ClientOrg } from '@/api/schemasMembers';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDate } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { ClientStatusBadge } from './ClientStatusBadge';
import { ReactivateClientDialog, SuspendClientDialog } from './ClientStatusDialogs';

/** Status of a client: active or suspended (with when and why), and the action that flips it. */
export function ClientStatusCard({ org, id, index }: { org: ClientOrg; id: string; index: number }) {
  const [dialog, setDialog] = useState<'suspend' | 'reactivate' | null>(null);
  return (
    <Card id={id} data-testid="client-status" className="enter scroll-mt-24" style={stagger(index)}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          Status
          <ClientStatusBadge status={org.status} testId="client-status-badge" />
        </CardTitle>
        <CardDescription>A suspended client keeps everything, but nobody in it can use Hangar.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <StatusFacts org={org} />
        <StatusAction org={org} onOpen={setDialog} />
      </CardContent>
      <SuspendClientDialog org={dialog === 'suspend' ? org : null} onDone={() => setDialog(null)} />
      <ReactivateClientDialog org={dialog === 'reactivate' ? org : null} onDone={() => setDialog(null)} />
    </Card>
  );
}

function StatusFacts({ org }: { org: ClientOrg }) {
  if (org.status === 'active') return <p className="max-w-[64ch] text-[13px] text-muted-foreground">Its people, API tokens and CI pipelines use Hangar normally.</p>;
  if (org.status !== 'suspended') return <p className="max-w-[64ch] text-[13px] text-muted-foreground">Its deletion has started: see the danger zone below.</p>;
  return (
    <dl data-testid="client-suspension" className="grid min-w-0 gap-x-4 gap-y-1 text-[13px] sm:grid-cols-[auto_minmax(0,1fr)]">
      <dt className="text-muted-foreground">Suspended</dt>
      <dd className="text-foreground">{org.suspension ? formatDate(org.suspension.at) : '—'}</dd>
      <dt className="text-muted-foreground">Reason</dt>
      <dd data-testid="client-suspension-reason" className="break-words text-foreground">{org.suspension?.reason ?? '—'}</dd>
    </dl>
  );
}

function StatusAction({ org, onOpen }: { org: ClientOrg; onOpen: (d: 'suspend' | 'reactivate') => void }) {
  if (org.status === 'active') {
    return (
      <Button variant="outline" data-testid="client-suspend-open" className="shrink-0 max-sm:h-11 text-destructive hover:text-destructive" onClick={() => onOpen('suspend')}>
        <Icon name="pause_circle" size={18} />
        Suspend client
      </Button>
    );
  }
  if (org.status !== 'suspended') return null;
  return (
    <Button data-testid="client-reactivate-open" className="shrink-0 max-sm:h-11" onClick={() => onOpen('reactivate')}>
      <Icon name="play_circle" size={18} />
      Reactivate client
    </Button>
  );
}
